import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip } from "recharts";
import api, { apiError, fileUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import { StatusChip } from "@/components/StatusChip";
import Footer from "@/components/Footer";
import { ShieldCheck, Trophy, Users, DeviceMobile, Globe, ChartLineUp, Broadcast, ArrowsOut, X, Pause, Play, Timer, QrCode, Sun, Moon, IdentificationCard, MapPin, Bell, CheckCircle } from "@phosphor-icons/react";

const REFRESH_MS = 10000;
const fmtHour = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric" }) : "");

function useCountdown(endsAt) {
  const [remaining, setRemaining] = useState(() => (endsAt ? new Date(endsAt).getTime() - Date.now() : 0));
  useEffect(() => {
    if (!endsAt) return;
    const tick = () => setRemaining(new Date(endsAt).getTime() - Date.now());
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  if (!endsAt || remaining <= 0) return null;
  const s = Math.floor(remaining / 1000);
  const d = Math.floor(s / 86400);
  const hh = String(Math.floor((s % 86400) / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return { d, text: `${d > 0 ? d + "d " : ""}${hh}:${mm}:${ss}` };
}

function Ring({ pct, brand }) {
  const r = 54, c = 2 * Math.PI * r;
  const off = c - (Math.min(100, pct) / 100) * c;
  return (
    <div className="relative mx-auto" style={{ width: 150, height: 150 }}>
      <svg width="150" height="150" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="75" cy="75" r={r} fill="none" stroke="#EDECE7" strokeWidth="12" />
        <circle cx="75" cy="75" r={r} fill="none" stroke={brand} strokeWidth="12" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 0.8s cubic-bezier(0.16,1,0.3,1)" }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-display font-bold tabular-nums text-4xl text-slate-900">{pct}%</span>
      </div>
    </div>
  );
}

const TONES = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  slate: "bg-slate-50 text-slate-600 border-slate-200",
};

export default function LiveMonitor({ embedded = false }) {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [pulse, setPulse] = useState(false);
  const [screen, setScreen] = useState(false);
  const [rotate, setRotate] = useState(true);
  const [rotIdx, setRotIdx] = useState(0);
  const [projLight, setProjLight] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/live/${id}`);
      setData(data);
      setError("");
      setPulse(true);
      setTimeout(() => setPulse(false), 900);
    } catch (e) {
      setError(apiError(e.response?.data?.detail));
    }
  }, [id]);

  useEffect(() => {
    load();
    timer.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer.current);
  }, [load]);

  const enterScreen = async () => {
    setScreen(true);
    try { await document.documentElement.requestFullscreen?.(); } catch (e) { console.debug("Fullscreen request denied:", e); }
  };
  const exitScreen = async () => {
    setScreen(false);
    try { if (document.fullscreenElement) await document.exitFullscreen?.(); } catch (e) { console.debug("Exit fullscreen failed:", e); }
  };
  useEffect(() => {
    const onFs = () => { if (!document.fullscreenElement) setScreen(false); };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const positionsCount = data?.results?.length || 0;
  useEffect(() => {
    if (!screen || !rotate || positionsCount <= 1) return;
    const t = setInterval(() => setRotIdx((i) => (i + 1) % positionsCount), 8000);
    return () => clearInterval(t);
  }, [screen, rotate, positionsCount]);
  useEffect(() => { if (screen) setRotIdx(0); }, [screen]);

  const countdown = useCountdown(data?.election?.ends_at);

  const wrap = (node) => (embedded ? <Layout back={`/app/elections/${id}`}>{node}</Layout> : (
    <div className="min-h-[100dvh] bg-[#F7F8FA] grain" data-testid="live-monitor-page">{node}<Footer /></div>
  ));

  if (error) return wrap(
    <div className="min-h-[40vh] flex items-center justify-center p-6 text-center" data-testid="live-error">
      <div><h1 className="font-display font-bold text-2xl tracking-tight mb-2">Live monitor unavailable</h1><p className="text-zinc-500">{error}</p></div>
    </div>
  );
  if (!data) return wrap(<div className="min-h-[40vh] flex items-center justify-center text-zinc-400 font-mono text-sm" data-testid="live-loading">loading…</div>);

  const brand = data.org?.branding?.primary_color || "#1F6FE5";
  const t = data.turnout;
  const ch = data.channels;
  const isOpen = data.election.status === "live";
  const closed = data.election.status === "closed";
  const registerUrl = `${window.location.origin}/register/${data.election.id}`;
  const canRegister = data.election.allow_self_registration && isOpen;

  const chans = [
    { key: "web", label: "Web", val: ch.web || 0, icon: Globe },
    { key: "ussd", label: "USSD", val: ch.ussd || 0, icon: DeviceMobile },
    { key: "kiosk", label: "Kiosk", val: ch.kiosk || 0, icon: IdentificationCard },
  ];
  const chMax = Math.max(1, ...chans.map((c) => c.val));
  const stations = data.stations || [];
  const stationMax = Math.max(1, ...stations.map((s) => s.total));

  const alerts = [];
  if (closed) alerts.push({ tone: "slate", icon: CheckCircle, text: "Voting has closed", sub: "Counts are final" });
  else if (isOpen) alerts.push({ tone: "green", icon: Broadcast, text: "Voting is live", sub: "Updates every 10 seconds" });
  if (isOpen && countdown) alerts.push({ tone: "amber", icon: Timer, text: `Closes in ${countdown.text}`, sub: "Voting window ending soon" });
  [75, 50, 25].forEach((m) => { if (t.pct >= m) alerts.push({ tone: "blue", icon: ChartLineUp, text: `Turnout passed ${m}%`, sub: `${t.voted} of ${t.total} ballots cast` }); });
  if ((ch.ussd || 0) > 0) alerts.push({ tone: "slate", icon: DeviceMobile, text: `${ch.ussd} USSD ballots`, sub: "Feature-phone channel active" });
  const shownAlerts = alerts.slice(0, 6);

  const tileBase = "bg-white rounded-2xl shadow-sm border border-slate-200 p-6";

  const projector = screen && (
    <div className={`fixed inset-0 z-[60] overflow-y-auto ${projLight ? "bg-[#F7F8F9] text-zinc-900" : "bg-[#09090B] text-white"}`} data-testid="live-projector">
      <div className="absolute top-5 right-5 z-10 flex items-center gap-2">
        <button data-testid="live-projector-theme" onClick={() => setProjLight((v) => !v)} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm transition-colors ${projLight ? "border-zinc-300 hover:bg-black/5" : "border-white/20 hover:bg-white/10"}`}>
          {projLight ? <><Moon size={16} /> Dark</> : <><Sun size={16} /> Light</>}
        </button>
        <button data-testid="live-projector-close" onClick={exitScreen} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm transition-colors ${projLight ? "border-zinc-300 hover:bg-black/5" : "border-white/20 hover:bg-white/10"}`}>
          <X size={16} /> Exit
        </button>
      </div>
      {canRegister && (
        <div className={`absolute top-5 left-5 z-10 flex items-center gap-3 rounded-lg p-2.5 ${projLight ? "bg-white border border-zinc-200" : "bg-white"}`} data-testid="live-projector-register-qr">
          <QRCodeCanvas value={registerUrl} size={92} level="M" fgColor="#0A0A0A" />
          <div className="pr-2">
            <div className="text-[9px] uppercase tracking-widest text-zinc-500">Scan to</div>
            <div className="font-display font-bold text-zinc-900 leading-tight text-lg">register<br />&amp; vote</div>
          </div>
        </div>
      )}
      {closed && data.winners.length > 0 ? (
        <div className="relative min-h-full flex flex-col justify-center items-center px-[6vw] py-[8vh] overflow-hidden" data-testid="live-winner-reveal">
          {Array.from({ length: 36 }).map((_, i) => {
            const colors = [brand, "#F59E0B", "#EC4899", "#10B981", "#6366F1", "#EF4444"];
            return <span key={i} className="vf-confetti" style={{ left: `${(i * 2.8) % 100}%`, background: colors[i % colors.length], animationDuration: `${3 + (i % 5) * 0.7}s`, animationDelay: `${(i % 9) * 0.35}s` }} />;
          })}
          <div className="relative z-10 text-center mb-10">
            <div className="vf-trophy inline-block mb-4"><Trophy size={72} weight="fill" style={{ color: brand }} /></div>
            <div className="uppercase tracking-[0.4em] text-sm mb-2" style={{ color: brand }}>Official Results</div>
            <h1 className="font-display font-black tracking-tighter leading-[0.95]" style={{ fontSize: "clamp(2.5rem,7vw,7rem)" }}>{data.election.title}</h1>
          </div>
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 w-full max-w-6xl">
            {data.winners.map((w, i) => (
              <div key={w.position} className={`vf-pop-in rounded-2xl p-6 text-center border ${projLight ? "bg-white border-zinc-200 shadow-sm" : "bg-white/[0.04] border-white/10"}`} style={{ animationDelay: `${0.15 + i * 0.15}s` }}>
                {w.photo_url ? <img src={fileUrl(w.photo_url)} alt="" className="w-20 h-20 rounded-full object-cover mx-auto mb-3" style={{ boxShadow: `0 0 0 3px ${brand}` }} /> : <div className="w-20 h-20 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: brand + "22" }}><Trophy size={30} weight="fill" style={{ color: brand }} /></div>}
                <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">{w.position}</div>
                <div className="font-display font-bold tracking-tight" style={{ fontSize: "clamp(1.1rem,2vw,1.6rem)" }}>{w.name}</div>
                <div className="inline-flex items-center gap-1 mt-2 text-xs font-semibold uppercase tracking-widest" style={{ color: brand }}><Trophy size={12} weight="fill" /> Winner</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="min-h-full flex flex-col justify-center px-[6vw] py-[6vh]">
          <div className="flex items-center gap-3 mb-2">
            <span className={`w-3 h-3 rounded-full ${pulse ? "scale-125" : ""} transition-transform`} style={{ background: isOpen ? "#16a34a" : "#a1a1aa" }} />
            <span className={`text-sm uppercase tracking-[0.3em] font-mono ${projLight ? "text-zinc-500" : "text-zinc-400"}`}>{data.org.name} · {isOpen ? "Live" : data.election.status}</span>
          </div>
          <h1 className="font-display font-black tracking-tighter leading-[0.95] mb-8" style={{ fontSize: "clamp(2.5rem,6vw,6rem)" }}>{data.election.title}</h1>
          {isOpen && countdown && (
            <div className="mb-8 inline-flex items-center gap-3" data-testid="live-projector-countdown">
              <span className={`uppercase tracking-[0.3em] text-sm ${projLight ? "text-zinc-500" : "text-zinc-400"}`}>Closes in</span>
              <span className="font-mono font-bold tabular-nums" style={{ fontSize: "clamp(1.5rem,4vw,3rem)", color: brand }}>{countdown.text}</span>
            </div>
          )}
          <div className="flex flex-wrap items-end gap-x-16 gap-y-6 mb-10">
            <div>
              <div className="font-display font-black tabular-nums leading-none" style={{ fontSize: "clamp(3rem,10vw,9rem)", color: brand }}>{t.pct}%</div>
              <div className={`uppercase tracking-[0.3em] text-sm mt-2 ${projLight ? "text-zinc-500" : "text-zinc-400"}`}>Turnout · {t.voted} / {t.total}</div>
            </div>
            <div className="flex gap-10">
              <div><div className="font-display font-bold tabular-nums" style={{ fontSize: "clamp(2rem,5vw,4rem)" }}>{ch.web}</div><div className="text-zinc-500 uppercase tracking-widest text-xs mt-1">Web</div></div>
              <div><div className="font-display font-bold tabular-nums" style={{ fontSize: "clamp(2rem,5vw,4rem)" }}>{ch.ussd}</div><div className="text-zinc-500 uppercase tracking-widest text-xs mt-1">USSD</div></div>
              <div><div className="font-display font-bold tabular-nums" style={{ fontSize: "clamp(2rem,5vw,4rem)" }}>{ch.kiosk || 0}</div><div className="text-zinc-500 uppercase tracking-widest text-xs mt-1">Kiosk</div></div>
            </div>
          </div>
          {positionsCount > 1 && (
            <div className="flex items-center gap-3 mb-6" data-testid="live-projector-controls">
              <button onClick={() => setRotate((r) => !r)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs transition-colors ${projLight ? "border-zinc-300 hover:bg-black/5" : "border-white/20 hover:bg-white/10"}`} data-testid="live-rotate-toggle">
                {rotate ? <><Pause size={13} weight="fill" /> Pause rotation</> : <><Play size={13} weight="fill" /> Auto-rotate</>}
              </button>
              {rotate && (
                <div className="flex items-center gap-1.5">
                  {data.results.map((r, i) => (
                    <span key={r.position_id} className="w-2 h-2 rounded-full transition-all" style={{ background: i === rotIdx ? brand : (projLight ? "#d4d4d8" : "#3f3f46"), width: i === rotIdx ? 18 : 8 }} />
                  ))}
                </div>
              )}
            </div>
          )}
          <div className={rotate && positionsCount > 1 ? "" : "grid grid-cols-1 lg:grid-cols-2 gap-x-14 gap-y-8"}>
            {(rotate && positionsCount > 1 ? [data.results[rotIdx]] : data.results).map((r) => {
              const isRanked = r.ballot_type === "ranked";
              const max = Math.max(1, ...r.tally.map((c) => (isRanked ? c.points : c.votes)));
              return (
                <div key={r.position_id} className={rotate && positionsCount > 1 ? "vf-fade-up" : ""}>
                  <div className="flex items-baseline justify-between mb-3">
                    <h3 className="font-display font-bold tracking-tight" style={{ fontSize: "clamp(1.25rem,2.5vw,2rem)" }}>{r.title}</h3>
                    <span className="text-zinc-500 font-mono text-sm">{r.total_ballots} ballots</span>
                  </div>
                  <div className="space-y-3">
                    {r.tally.slice(0, rotate && positionsCount > 1 ? 6 : 4).map((c, i) => {
                      const v = isRanked ? c.points : c.votes;
                      const leading = i === 0 && r.total_ballots > 0;
                      return (
                        <div key={c.id}>
                          <div className="flex items-center justify-between mb-1" style={{ fontSize: "clamp(0.9rem,1.6vw,1.4rem)" }}>
                            <span className="flex items-center gap-2 font-medium">{leading && <Trophy size={20} weight="fill" style={{ color: brand }} />}{c.name}</span>
                            <span className={`font-mono tabular-nums ${projLight ? "text-zinc-500" : "text-zinc-300"}`}>{isRanked ? `${c.points} pts` : `${c.votes} · ${c.pct}%`}</span>
                          </div>
                          <div className={`h-3 rounded-full overflow-hidden ${projLight ? "bg-zinc-200" : "bg-white/10"}`}>
                            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(v / max) * 100}%`, background: leading ? brand : (projLight ? "#a1a1aa" : "#52525b") }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  const content = (
    <div data-testid="live-monitor-content">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <StatusChip status={data.election.status} />
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400">
              <span className={`w-2 h-2 rounded-full ${pulse ? "scale-125" : ""} transition-transform`} style={{ background: isOpen ? "#16a34a" : "#a1a1aa" }} />
              {isOpen ? "Live · auto-updating" : "Snapshot"}
            </span>
          </div>
          <h1 className="font-display font-black text-4xl tracking-tight text-slate-900" data-testid="live-title">{data.election.title}</h1>
        </div>
        <button data-testid="live-bigscreen-btn" onClick={enterScreen} className="inline-flex items-center gap-1.5 px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm font-medium hover:border-[#1F6FE5] hover:text-[#1F6FE5] transition-colors">
          <ArrowsOut size={16} /> Big screen
        </button>
      </div>

      {/* Bento grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        {/* Turnout */}
        <div className={`${tileBase} md:col-span-1 flex flex-col`} data-testid="bento-turnout">
          <div className="flex items-center gap-2 text-slate-400 text-[10px] uppercase tracking-widest mb-4"><Users size={14} weight="fill" /> Turnout</div>
          <Ring pct={t.pct} brand={brand} />
          <div className="mt-4 text-center font-mono text-sm text-slate-500">{t.voted} / {t.total} voters</div>
          {isOpen && countdown && (
            <div className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2" data-testid="live-countdown">
              <Timer size={16} weight="duotone" className="text-amber-600" />
              <span className="font-mono text-sm font-bold tabular-nums text-amber-700">{countdown.text}</span>
            </div>
          )}
        </div>

        {/* Votes over time */}
        <div className={`${tileBase} md:col-span-3`} data-testid="bento-overtime">
          <h3 className="font-display text-lg text-slate-900 mb-4">Votes over time</h3>
          {data.timeline.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={data.timeline} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="turnoutGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={brand} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={brand} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="hour" tickFormatter={(h) => (fmtHour(h).split(",")[1] || "").trim()} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} minTickGap={24} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} width={36} />
                <RTooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }} labelFormatter={(h) => fmtHour(h)} formatter={(v) => [v, "votes"]} />
                <Area type="monotone" dataKey="count" stroke={brand} strokeWidth={2.5} fill="url(#turnoutGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-sm text-slate-400">No votes recorded yet.</div>
          )}
        </div>

        {/* By channel */}
        <div className={`${tileBase} md:col-span-2`} data-testid="bento-channels">
          <h3 className="font-display text-lg text-slate-900 mb-4">By channel</h3>
          <div className="space-y-4">
            {chans.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.key} data-testid={`channel-bar-${c.key}`}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="flex items-center gap-2 font-medium text-slate-700"><Icon size={16} weight="duotone" style={{ color: brand }} /> {c.label}</span>
                    <span className="font-mono tabular-nums text-slate-500">{c.val}</span>
                  </div>
                  <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(c.val / chMax) * 100}%`, background: brand }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top stations */}
        <div className={`${tileBase} md:col-span-1 relative overflow-hidden`} data-testid="bento-stations">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg text-slate-900">Top stations</h3>
            {stations.length === 0 && <span className="text-[9px] uppercase tracking-wider font-bold bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded">Soon</span>}
          </div>
          {stations.length > 0 ? (
            <div className="space-y-3" data-testid="station-list">
              {stations.slice(0, 5).map((s, i) => (
                <div key={s.name} className="flex items-center justify-between text-sm" data-testid={`station-row-${i}`}>
                  <span className="flex items-center gap-2 text-slate-700 min-w-0"><MapPin size={15} weight="duotone" style={{ color: brand }} /><span className="truncate">{s.name}</span></span>
                  <span className="font-mono tabular-nums text-slate-500 shrink-0">{s.pct}% · {s.voted}/{s.total}</span>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="space-y-3 opacity-50 blur-[1px] select-none pointer-events-none">
                {["Station A", "Station B", "Station C"].map((s, i) => (
                  <div key={s} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-slate-600"><MapPin size={15} weight="duotone" /> {s}</span>
                    <span className="font-mono text-slate-400">{[42, 31, 18][i]}%</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-3">Tag voters with a polling station (CSV, manual, or self-registration) to rank turnout here.</p>
            </>
          )}
        </div>

        {/* Alerts */}
        <div className={`${tileBase} md:col-span-1`} data-testid="bento-alerts">
          <div className="flex items-center gap-2 mb-4"><Bell size={17} weight="duotone" style={{ color: brand }} /><h3 className="font-display text-lg text-slate-900">Alerts</h3></div>
          <div className="space-y-2.5">
            {shownAlerts.map((a, i) => {
              const Icon = a.icon;
              return (
                <div key={i} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 ${TONES[a.tone]}`} data-testid={`alert-${i}`}>
                  <Icon size={16} weight="fill" className="mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium leading-tight">{a.text}</div>
                    <div className="text-[11px] opacity-80">{a.sub}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Turnout by station / map */}
        <div className={`${tileBase} md:col-span-2 relative overflow-hidden min-h-[220px]`} data-testid="bento-map">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg text-slate-900">Turnout by station</h3>
            {stations.length === 0 && <span className="text-[9px] uppercase tracking-wider font-bold bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded">Soon</span>}
          </div>
          {stations.length > 0 ? (
            <div className="space-y-3" data-testid="station-bars">
              {stations.slice(0, 6).map((s) => (
                <div key={s.name}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="flex items-center gap-2 font-medium text-slate-700 min-w-0"><MapPin size={14} weight="fill" style={{ color: brand }} /><span className="truncate">{s.name}</span></span>
                    <span className="font-mono tabular-nums text-slate-500 shrink-0">{s.voted}/{s.total} · {s.pct}%</span>
                  </div>
                  <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(s.voted / stationMax) * 100}%`, background: brand }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="absolute inset-0 top-14 opacity-[0.35]" aria-hidden>
                <svg viewBox="0 0 400 200" className="w-full h-full">
                  {Array.from({ length: 9 }).map((_, r) => Array.from({ length: 18 }).map((_, c2) => (
                    <circle key={`${r}-${c2}`} cx={12 + c2 * 22} cy={14 + r * 21} r={((r * c2) % 4 === 0) ? 4 : 2.2} fill={brand} opacity={((r + c2) % 3 === 0) ? 0.9 : 0.35} />
                  )))}
                </svg>
              </div>
              <div className="absolute inset-x-0 bottom-4 text-center">
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-full px-3 py-1">
                  <MapPin size={13} weight="fill" style={{ color: brand }} /> Assign polling stations to see turnout split here
                </span>
              </div>
            </>
          )}
        </div>

        {/* Register QR or transparency note */}
        {canRegister ? (
          <div className={`${tileBase} md:col-span-2 flex items-center gap-5`} data-testid="bento-register">
            <div className="bg-white p-2.5 rounded-xl border border-slate-200 shrink-0">
              <QRCodeCanvas value={registerUrl} size={104} level="M" includeMargin fgColor="#0A0A0A" />
            </div>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 mb-1"><QrCode size={13} weight="fill" style={{ color: brand }} /> In-person voting</div>
              <h3 className="font-display text-lg text-slate-900 mb-1">Scan to register &amp; vote</h3>
              <p className="text-sm text-slate-500">Point a phone camera at this code to register and cast a ballot. Each voter gets a single-use link.</p>
            </div>
          </div>
        ) : (
          <div className={`${tileBase} md:col-span-2 flex items-center gap-3`} data-testid="bento-transparency">
            <ShieldCheck size={30} weight="duotone" style={{ color: brand }} className="shrink-0" />
            <div>
              <h3 className="font-display text-lg text-slate-900">Anonymous &amp; transparent</h3>
              <p className="text-sm text-slate-500">These live counts carry no voter identity. Ballots stay secret while turnout stays public.</p>
            </div>
          </div>
        )}
      </div>

      {/* Per-position tallies */}
      {data.election.results_visible === false && !closed ? (
        <div className={`${tileBase} mt-5 flex items-center gap-3`} data-testid="results-hidden-note">
          <ShieldCheck size={26} weight="duotone" style={{ color: brand }} className="shrink-0" />
          <div>
            <h3 className="font-display text-lg text-slate-900">Results are sealed until voting closes</h3>
            <p className="text-sm text-slate-500">The organiser chose to reveal counts only after close. Live turnout stays visible above; per-candidate tallies appear here once the election ends.</p>
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
        {data.results.map((r) => {
          const isRanked = r.ballot_type === "ranked";
          const max = Math.max(1, ...r.tally.map((c) => (isRanked ? c.points : c.votes)));
          return (
            <div key={r.position_id} className={tileBase} data-testid={`live-position-${r.position_id}`}>
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-display text-lg text-slate-900">{r.title}</h3>
                {!closed && r.total_ballots > 0 && <span className="text-[10px] uppercase tracking-widest text-slate-400">Provisional</span>}
              </div>
              <p className="text-xs font-mono text-slate-400 mb-4">{r.total_ballots} ballots · {isRanked ? "Borda points" : "Single choice"}</p>
              <div className="space-y-3">
                {r.tally.map((c, i) => {
                  const v = isRanked ? c.points : c.votes;
                  const leading = i === 0 && r.total_ballots > 0;
                  return (
                    <div key={c.id}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="flex items-center gap-1.5 font-medium">
                          {leading && <Trophy size={14} weight="fill" style={{ color: brand }} />}{c.name}
                          {leading && !closed && <span className="text-[9px] uppercase tracking-widest text-slate-400 ml-1">leading</span>}
                        </span>
                        <span className="font-mono text-slate-500 tabular-nums">{isRanked ? `${c.points} pts` : `${c.votes} · ${c.pct}%`}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(v / max) * 100}%`, background: leading ? brand : "#cbd5e1" }} />
                      </div>
                    </div>
                  );
                })}
                {r.total_ballots === 0 && <p className="text-sm text-slate-400 italic">No votes yet.</p>}
              </div>
            </div>
          );
        })}
      </div>
      )}

      <p className="mt-8 text-center text-xs text-slate-400 max-w-md mx-auto flex items-center justify-center gap-1.5">
        <ShieldCheck size={14} weight="fill" style={{ color: brand }} /> Ballots are anonymous. These live counts carry no voter identity.
      </p>
    </div>
  );

  if (embedded) {
    return (<>{projector}<Layout back={`/app/elections/${id}`}>{content}</Layout></>);
  }

  return (
    <div className="min-h-[100dvh] bg-[#F7F8FA] grain" data-testid="live-monitor-page">
      {projector}
      <header className="backdrop-blur-xl bg-white/80 border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center gap-2">
          {data.org?.branding?.logo_url ? <img src={fileUrl(data.org.branding.logo_url)} alt="" className="h-7" /> : <div className="w-6 h-6 rounded-[6px]" style={{ background: brand }} />}
          <span className="font-semibold text-sm">{data.org.name}</span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono">
            <span className={`w-2 h-2 rounded-full ${pulse ? "scale-125" : ""} transition-transform`} style={{ background: isOpen ? "#16a34a" : "#a1a1aa" }} />
            {isOpen ? "Live · auto-updating" : "Snapshot"}
          </span>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-6 py-10 relative z-10">{content}</div>
      <Footer />
    </div>
  );
}
